import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Drawer } from '@components/ui/drawer/component';
import { useIsPhone } from '@hooks/useIsPhone';
import { Slot } from '@radix-ui/react-slot';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RemoveConfirmationAlertProps = {
  title: ReactNode;
  description: ReactNode;
  onConfirm?: () => void;
  onCancel?: () => void;
  onDeactivate?: () => void;
  children?: ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  handleOpen?: (open: boolean) => void;
  locked?: boolean;
};

export const RemoveConfirmationAlert = ({
  title,
  description,
  onConfirm,
  onDeactivate,
  onCancel,
  children,
  open,
  setOpen,
  handleOpen,
  locked,
}: RemoveConfirmationAlertProps) => {
  const state = useState(false);
  const [openDialog, setOpenDialog] =
    open && (handleOpen || setOpen) ? [open, handleOpen || setOpen] : state;

  const { t } = useTranslation('app');
  const isPhone = useIsPhone();

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (setOpenDialog) setOpenDialog(false);
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    if (setOpenDialog) setOpenDialog(false);
  };

  const handleDeactivate = () => {
    if (onDeactivate) onDeactivate();
    if (setOpenDialog) setOpenDialog(false);
  };

  const trigger = children ? (
    children
  ) : (
    <Button variant="ghost" size="sm" className="w-full justify-start whitespace-nowrap px-2">
      {t('actions.remove')}
    </Button>
  );

  if (isPhone)
    return (
      <>
        {!handleOpen && !setOpen && <Slot onClick={() => setOpenDialog?.(true)}>{trigger}</Slot>}
        <Drawer
          phoneSheet
          open={Boolean(openDialog)}
          setOpen={(value) => setOpenDialog?.(value)}
          title={title}>
          <div className="flex flex-col gap-5 px-4 pt-4">
            <div className="text-sm text-neutral-700">{description}</div>
            <div className="flex flex-col gap-2">
              <Button onClick={handleConfirm} variant="danger" size="lg" disabled={locked}>
                {t('actions.confirm')}
              </Button>
              {onDeactivate && (
                <Button onClick={handleDeactivate} variant="secondary" size="lg" disabled={locked}>
                  {t('actions.deactivate')}
                </Button>
              )}
              <Button onClick={handleCancel} variant="secondary" size="lg" disabled={locked}>
                {t('actions.cancel')}
              </Button>
            </div>
          </div>
        </Drawer>
      </>
    );

  return (
    <AlertDialog open={openDialog} onOpenChange={setOpenDialog}>
      {!handleOpen && !setOpen && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={handleCancel} variant="secondary" disabled={locked}>
            {t('actions.cancel')}
          </Button>
          <Button onClick={handleConfirm} variant="danger" disabled={locked}>
            {t('actions.confirm')}
          </Button>
          {onDeactivate && (
            <Button onClick={handleDeactivate} variant="secondary" disabled={locked}>
              {t('actions.deactivate')}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
